import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from "@/lib/utils";

export type BadgeVariant =
  | 'default'
  | 'secondary'
  | 'destructive'
  | 'outline'
  | 'green'
  | 'blue'
  | 'yellow'
  | 'red'
  | 'gray'
  | 'orange'
  | 'purple'
  | 'pink'
  | 'indigo'
  | 'teal'
  | 'violet';

const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
        secondary: 'border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90',
        destructive:
          'border-transparent bg-red-500 text-white [a&]:hover:bg-red-700 dark:bg-red-700 dark:text-white',
        outline: 'text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
        green: 'border-transparent bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
        blue: 'border-transparent bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
        yellow: 'border-transparent bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
        red: 'border-transparent bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
        gray: 'border-transparent bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
        orange: 'border-transparent bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
        purple: 'border-transparent bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
        pink: 'border-transparent bg-pink-100 text-pink-700 dark:bg-pink-900 dark:text-pink-300',
        indigo: 'border-transparent bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300',
        teal: 'border-transparent bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300',
        violet: 'border-transparent bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-300',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'span';

  return <Comp data-slot="badge" className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export { Badge, badgeVariants };
